/**
 * Alpine.js Status Editor Component
 * Provides inline editing functionality for user status field
 */

import { editorBase } from './alpine-editor-base.js';
import {emoticonText} from './emoticons.js';
import { SM_BREAKPOINT, watchViewportBelow } from './breakpoints.js';

/**
 * Status Editor Component Factory
 * Handles user status updates
 */
export function statusEditor() {
    return {
        // State
        isEditing: false,
        // Follows the viewport. A host that draws the form both inline and in a mobile
        // sheet shows the sheet only when this is true.
        inSheet: false,
        value: '',
        originalValue: '',
        isSaving: false,
        error: null,
        
        // Configuration (set from data attributes or defaults)
        fieldName: 'status',
        endpoint: '/v1/user-status/self',
        maxLength: 140, // Twitter-like limit for status
        placeholder: 'What\'s on your mind?',
        
        // Computed properties
        get hasChanges() {
            return this.value !== this.originalValue;
        },
        
        get charCount() {
            return this.value.length;
        },
        
        get displayText() {
            return emoticonText(this.value);
        },

        get displayValue() {
            if (!this.value) {
                return '<span class="text-gray-400 italic">Add your status</span>';
            }
            return editorBase.escapeHtml(this.displayText);
        },
        
        // Methods
        getInput() {
            if (window.innerWidth < SM_BREAKPOINT && this.$refs.inputMobile) {
                return this.$refs.inputMobile;
            }
            return this.$refs.input;
        },

        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            this.endpoint = element.dataset.endpoint || this.endpoint;
            this.maxLength = parseInt(element.dataset.maxLength) || this.maxLength;
            this.placeholder = element.dataset.placeholder || this.placeholder;
            
            // The status is stored as literal text, and both hosts render it through
            // |e('html_attr'). The browser decodes the attribute, so dataset.value is
            // already that literal text. Decoding it again would read the user's own
            // text as markup: a status of `a<b c` parses as a tag and collapses to `a`,
            // and the next save would store the short text.
            let rawValue = element.dataset.value || '';

            // If no value on element, check for .inline-edit-content child (legacy)
            if (!rawValue) {
                const contentEl = element.querySelector('.inline-edit-content');
                if (contentEl) {
                    rawValue = contentEl.dataset.value || contentEl.textContent.trim() || '';
                    // Remove placeholder text if present
                    if (rawValue === 'Add your status') {
                        rawValue = '';
                    }
                }
            }

            this.originalValue = rawValue;
            this.value = this.originalValue;

            // Initialize base utilities
            editorBase.initEditor(this);
            // The page mounts one of these per status surface, so the add and the
            // remove are paired by hand on the same handler reference.
            this._savedFieldHandler = (event) => this.onFieldSaved(event.detail);
            window.addEventListener(editorBase.SAVED_EVENT, this._savedFieldHandler);
            this._stopSheetWatch = watchViewportBelow(SM_BREAKPOINT, (below) => {
                this.inSheet = below;
            });
        },

        destroy() {
            this._stopSheetWatch?.();
            if (this._savedFieldHandler) {
                window.removeEventListener(editorBase.SAVED_EVENT, this._savedFieldHandler);
                this._savedFieldHandler = null;
            }
        },

        // Another writer on the page (the post composer, a second status editor) saved
        // this field, so the next edit starts from the saved text. An open draft is the
        // owner's unsaved typing and survives; only the baseline moves, which keeps
        // hasChanges honest and stops a later Cancel from restoring the replaced text.
        onFieldSaved({ field, value }) {
            if (field !== this.fieldName) {
                return;
            }
            this.originalValue = value;
            if (!this.isEditing) {
                this.value = value;
            }
        },

        // Scroll lock belongs to the ui.bottom_sheet_* host, which locks while its
        // alpine_show expression is true. Every close must call cancel() or save().
        startEdit() {
            this.value = this.originalValue;
            this.isEditing = true;
            this.error = null;

            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }

            // Focus the input after Alpine updates the DOM
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const input = this.getInput();
                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            });
        },
        
        cancel() {
            this.value = this.originalValue;
            this.error = null;
            this.completeEdit();
        },
        
        completeEdit() {
            this.isEditing = false;

            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },
        
        updateCharCount() {
            // Character count is handled by the getter
        },
        
        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.cancel();
            } else if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.save();
            }
        },
        
        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            // The post composer trims too, so both writers store the same text.
            const submittedValue = this.value.trim();

            try {
                // Status endpoint expects 'message' field
                await editorBase.saveField(this.endpoint, 'message', submittedValue);

                // Save successful - update local state
                this.originalValue = submittedValue;
                this.value = submittedValue;

                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, submittedValue);
                }

                // Exit edit mode
                this.completeEdit();
                editorBase.emitSaved(this.fieldName, submittedValue);

            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || 'Failed to save status. Please try again.';
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}